export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

interface LogEntry {
  level: LogLevel;
  message: string;
  context?: string | undefined;
  timestamp: string;
  data?: unknown;
}

class Logger {
  private static instance: Logger;
  private logLevel: LogLevel;
  private isDevelopment: boolean;

  private constructor() {
    this.isDevelopment = import.meta.env.DEV;
    this.logLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.ERROR;
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.logLevel;
  }

  private formatMessage(entry: LogEntry): string {
    const prefix = `[${entry.timestamp}] ${LogLevel[entry.level]}`;
    const context = entry.context ? ` [${entry.context}]` : '';
    return `${prefix}${context}: ${entry.message}`;
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    context?: string,
    data?: unknown
  ): LogEntry {
    return {
      level,
      message,
      context,
      timestamp: new Date().toISOString(),
      data,
    };
  }

  private writeLog(entry: LogEntry): void {
    if (!this.shouldLog(entry.level)) return;

    const formattedMessage = this.formatMessage(entry);

    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(formattedMessage, entry.data || '');
        break;
      case LogLevel.INFO:
        console.info(formattedMessage, entry.data || '');
        break;
      case LogLevel.WARN:
        console.warn(formattedMessage, entry.data || '');
        break;
      case LogLevel.ERROR:
        console.error(formattedMessage, entry.data || '');
        break;
    }
  }

  public debug(message: string, context?: string, data?: unknown): void {
    const entry = this.createLogEntry(LogLevel.DEBUG, message, context, data);
    this.writeLog(entry);
  }

  public info(message: string, context?: string, data?: unknown): void {
    const entry = this.createLogEntry(LogLevel.INFO, message, context, data);
    this.writeLog(entry);
  }

  public warn(message: string, context?: string, data?: unknown): void {
    const entry = this.createLogEntry(LogLevel.WARN, message, context, data);
    this.writeLog(entry);
  }

  public error(message: string, context?: string, data?: unknown): void {
    const entry = this.createLogEntry(LogLevel.ERROR, message, context, data);
    this.writeLog(entry);
  }

  public setLogLevel(level: LogLevel): void {
    this.logLevel = level;
  }
}

export const logger = Logger.getInstance();

export const createContextLogger = (context: string) => ({
  debug: (message: string, data?: unknown) => logger.debug(message, context, data),
  info: (message: string, data?: unknown) => logger.info(message, context, data),
  warn: (message: string, data?: unknown) => logger.warn(message, context, data),
  error: (message: string, data?: unknown) => logger.error(message, context, data),
});